import React from "react";
import { Check, Copy, Bookmark, BookmarkPlus, Eye } from "lucide-react";
import { toast } from "sonner";
import { ComponentItem } from "./types";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

interface ComponentCardProps {
  component: ComponentItem;
  viewMode: "grid" | "list";
  animateCards: boolean;
  copied: string | null;
  setCopied: (id: string | null) => void;
  onOpenDetails: (component: ComponentItem) => void;
  isFavorite?: boolean;
  onToggleFavorite?: (e: React.MouseEvent) => void;
  previewMode: 'light' | 'dark';
  isLoggedIn: boolean;
}

const ComponentCard: React.FC<ComponentCardProps> = ({
  component,
  viewMode,
  animateCards,
  copied,
  setCopied,
  onOpenDetails,
  isFavorite = false,
  onToggleFavorite,
  previewMode,
  isLoggedIn
}) => {
  const copyToClipboard = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(component.code);
    setCopied(component.id);
    toast.success("Code copied to clipboard!");
    
    setTimeout(() => {
      setCopied(null);
    }, 2000);
  };

  // Complexity color based on level
  const complexityColors = {
    simple: "bg-green-500/10 text-green-500",
    medium: "bg-amber-500/10 text-amber-500",
    complex: "bg-red-500/10 text-red-500"
  };

  return (
    <Card
      className={`overflow-hidden h-full transition-all duration-300 ${
        animateCards ? "animate-in fade-in zoom-in-95" : ""
      } ${viewMode === "list" ? "flex" : "flex flex-col"} 
      group hover:border-primary/40 hover:shadow-md hover:shadow-primary/10 hover:bg-secondary/30 cursor-pointer`}
      onClick={() => onOpenDetails(component)}
    >
      <div 
        className={`${viewMode === "list" ? "w-1/3 border-r border-border" : "w-full border-b border-border"} bg-black/30 relative overflow-hidden group-hover:border-primary/20`}
      >
        <div 
          className={`relative aspect-video flex items-center justify-center overflow-hidden transition-colors bg-black/50`}
        >
          <div 
            dangerouslySetInnerHTML={{ __html: component.preview }} 
            className="w-full h-full flex items-center justify-center scale-90 transition-transform duration-300 group-hover:scale-95"
            onClick={(e) => {
              // Prevent default behavior for all links inside preview
              e.preventDefault();
              // Stop propagation to prevent parent card click
              e.stopPropagation();
            }}
          />
        </div>
      </div>
      <CardContent className={`p-4 flex flex-col justify-between flex-grow ${viewMode === "list" ? "w-2/3" : "w-full"}`}>
        <div>
          <h3 className="font-medium mb-1 group-hover:text-primary transition-colors">{component.name}</h3>
          <p className="text-sm text-muted-foreground line-clamp-2 min-h-[40px]">
            {component.description || `A ${component.complexity || 'simple'} ${component.category} component for your project.`}
          </p>
        </div>
        
        <div className="mt-auto">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs px-2 py-1 bg-primary/10 text-primary rounded-full transition-colors hover:bg-primary/20">
                {component.category.charAt(0).toUpperCase() + component.category.slice(1)}
              </span>
              {component.complexity && (
                <span className={`text-xs px-2 py-1 rounded-full ${complexityColors[component.complexity]} transition-colors`}>
                  {component.complexity.charAt(0).toUpperCase() + component.complexity.slice(1)}
                </span>
              )}
            </div>
            <div className="flex items-center gap-1.5">
              {isLoggedIn && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    onToggleFavorite?.(e);
                  }}
                  className="p-1.5 text-muted-foreground hover:text-primary hover:bg-primary/10 rounded-full transition-all hover:scale-110 active:scale-95"
                  title={isFavorite ? "Remove from favorites" : "Add to favorites"}
                >
                  {isFavorite ? <Bookmark size={16} className="fill-primary text-primary" /> : <BookmarkPlus size={16} />}
                </button>
              )}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  copyToClipboard(e);
                }}
                className="p-1.5 text-muted-foreground hover:text-primary hover:bg-primary/10 rounded-full transition-all hover:scale-110 active:scale-95"
                title="Copy code"
              >
                {copied === component.id ? <Check size={16} className="text-green-500" /> : <Copy size={16} />}
              </button>
            </div>
          </div>
          
          <Button 
            variant="outline" 
            size="sm" 
            className="w-full text-xs hover:bg-primary/10 hover:text-primary transition-all hover:border-primary/30 active:scale-[0.98]" 
            onClick={() => onOpenDetails(component)}
          >
            <Eye size={14} className="mr-1.5" />
            Preview
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default ComponentCard;
